'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import {
  Eye, EyeOff, Sparkles, Star, ArrowRight, Loader2, X,
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

type AuthMode = 'login' | 'signup';
type UserRole = 'icon' | 'enthusiast';
type LegalDoc = 'terms' | 'privacy' | null;

interface LoginFormData {
  email: string;
  password: string;
}

interface SignupFormData {
  displayName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
  agreeTerms: boolean;
}

// ─── Legal Drawer ────────────────────────────────────────────────────────────

function LegalDrawer({ doc, onClose }: { doc: LegalDoc; onClose: () => void }) {
  const isOpen = doc !== null;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-charcoal-900/40 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-lg bg-cream-100 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        role="dialog"
        aria-modal="true"
        aria-label={doc === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-cream-300 bg-white shrink-0">
          <div>
            <p className="text-xs text-charcoal-400 uppercase tracking-widest font-medium mb-0.5">Silhouette</p>
            <h2 className="font-editorial text-xl font-semibold text-charcoal-900">
              {doc === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-cream-200 hover:bg-cream-300 text-charcoal-600 hover:text-charcoal-900 transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto px-6 py-8 space-y-8 text-charcoal-700">
          {doc === 'terms' ? (
            <>
              <div>
                <p className="text-xs text-charcoal-400 mb-4">Last updated: April 2026</p>
                <p className="leading-relaxed text-charcoal-600">
                  Welcome to Silhouette. By using our platform, you agree to the following terms.
                </p>
              </div>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">1. User Responsibility</h3>
                <p className="leading-relaxed mb-3 text-sm">You are responsible for all content you post on Silhouette. You agree not to post:</p>
                <ul className="list-disc list-inside space-y-1.5 text-charcoal-600 text-sm">
                  <li>Illegal, harmful, or misleading content</li>
                  <li>Content that infringes on copyrights or intellectual property</li>
                  <li>Spam or unauthorized promotions</li>
                </ul>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">2. Content Ownership &amp; License</h3>
                <p className="leading-relaxed text-sm">
                  You retain ownership of your content. However, by posting on Silhouette, you grant us a non-exclusive, worldwide, royalty-free license to display, distribute, and promote your content on the platform.
                </p>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">3. Platform Rights</h3>
                <p className="leading-relaxed mb-3 text-sm">Silhouette reserves the right to:</p>
                <ul className="list-disc list-inside space-y-1.5 text-charcoal-600 text-sm">
                  <li>Remove any content at any time</li>
                  <li>Suspend or ban users who violate policies</li>
                  <li>Moderate content at our sole discretion</li>
                </ul>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">4. No Liability</h3>
                <p className="leading-relaxed text-sm">
                  Silhouette is not responsible for user-generated content. All content is provided "as is" without warranties of any kind.
                </p>
                <p className="leading-relaxed mt-3 text-sm">
                  To the fullest extent permitted by law, Silhouette shall not be liable for any damages arising from user-generated content, including but not limited to direct, indirect, incidental, punitive, or consequential damages.
                </p>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">5. Age Requirement</h3>
                <p className="leading-relaxed text-sm">
                  You must be at least 13 years old to use this platform. By using Silhouette, you confirm that you meet this age requirement.
                </p>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">6. Intellectual Property</h3>
                <p className="leading-relaxed text-sm">
                  You retain ownership of the content you post. By posting content on Silhouette, you grant us a non-exclusive, royalty-free, worldwide license to display, distribute, and promote your content within the platform.
                </p>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">7. Changes to Terms</h3>
                <p className="leading-relaxed text-sm">
                  Silhouette reserves the right to modify these Terms of Service at any time. Continued use of the platform after changes are posted constitutes your acceptance of the revised terms.
                </p>
              </section>
            </>
          ) : (
            <>
              <div>
                <p className="text-xs text-charcoal-400 mb-4">Last updated: April 2026</p>
                <p className="leading-relaxed text-charcoal-600">
                  We value your privacy and are committed to protecting your information.
                </p>
              </div>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">1. Information We Collect</h3>
                <p className="leading-relaxed mb-3 text-sm">We collect the following information when you use Silhouette:</p>
                <ul className="list-disc list-inside space-y-1.5 text-charcoal-600 text-sm">
                  <li>Email address</li>
                  <li>Username and profile details</li>
                  <li>Activity on the platform (posts, likes, follows)</li>
                </ul>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">2. How We Use Your Data</h3>
                <p className="leading-relaxed mb-3 text-sm">We use your data to:</p>
                <ul className="list-disc list-inside space-y-1.5 text-charcoal-600 text-sm">
                  <li>Provide and improve the platform</li>
                  <li>Personalize your experience</li>
                  <li>Maintain security and prevent abuse</li>
                  <li>Authenticate your identity and manage your account</li>
                  <li>Display your profile and content to other users</li>
                  <li>Enable social features such as following, liking, and saving</li>
                </ul>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">3. Third-Party Services</h3>
                <p className="leading-relaxed text-sm">
                  We use <strong>Supabase</strong> for authentication, database, and storage. Supabase processes your data only as necessary to provide the services described in this policy. Your data is stored securely in Supabase's infrastructure, hosted on AWS.
                </p>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">4. Data Control</h3>
                <p className="leading-relaxed text-sm">
                  Users may request account deletion or data removal at any time. To submit a request, contact us through the platform. We will process your request within a reasonable timeframe in accordance with applicable law.
                </p>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">5. Data Security</h3>
                <p className="leading-relaxed text-sm">
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
                </p>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">6. Cookies</h3>
                <p className="leading-relaxed text-sm">
                  Silhouette uses cookies and similar technologies to maintain your session and improve your experience. By using the platform, you consent to our use of cookies.
                </p>
              </section>
              <section>
                <h3 className="font-editorial text-lg font-semibold text-charcoal-900 mb-2">7. Changes to This Policy</h3>
                <p className="leading-relaxed text-sm">
                  We may update this Privacy Policy from time to time. We will notify users of significant changes by posting a notice on the platform. Continued use of Silhouette after changes are posted constitutes acceptance of the revised policy.
                </p>
              </section>
            </>
          )}
        </div>

        {/* Drawer Footer */}
        <div className="shrink-0 px-6 py-4 border-t border-cream-300 bg-white">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-charcoal-900 text-white rounded-xl text-sm font-semibold hover:bg-charcoal-800 active:scale-[0.98] transition-all duration-150"
          >
            Done — Return to Sign Up
          </button>
        </div>
      </div>
    </>
  );
}

// ─── AuthForm ─────────────────────────────────────────────────────────────────

export default function AuthForm() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>('enthusiast');
  const [legalDoc, setLegalDoc] = useState<LegalDoc>(null);

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '' },
  });

  const signupForm = useForm<SignupFormData>({
    defaultValues: { displayName: '', username: '', email: '', password: '', confirmPassword: '', role: 'enthusiast', agreeTerms: false },
  });

  const onLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back!');
      router.push('/runway-feed');
      router.refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const onSignup = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    if (!data.agreeTerms) {
      toast.error('Please agree to the terms');
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        displayName: data.displayName,
        username: data.username,
        role: selectedRole,
      });
      toast.success('Account created! Welcome to Silhouette.');
      router.push('/onboarding');
      router.refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-cream-100">
      <Toaster position="bottom-right" richColors />

      {/* Legal Side Drawer */}
      <LegalDrawer doc={legalDoc} onClose={() => setLegalDoc(null)} />

      {/* Left Panel */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[560px] shrink-0 bg-charcoal-900 text-white p-10 xl:p-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full"
            style={{ backgroundImage: 'repeating-linear-gradient(45deg, #c9a84c 0, #c9a84c 1px, transparent 0, transparent 50%)', backgroundSize: '20px 20px' }}
          />
        </div>
        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-gold-400 via-gold-300 to-gold-500" />

        <div className="flex items-center gap-3 relative z-10">
          <span
            className="flex items-center gap-2.5 select-none"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            <span
              className="text-5xl font-bold text-white"
              style={{ letterSpacing: '-0.02em', lineHeight: 1 }}
            >
              S
            </span>
            <span
              className="w-px h-6"
              style={{ background: 'rgba(255,255,255,0.35)' }}
              aria-hidden="true"
            />
            <span
              className="text-lg font-medium text-white"
              style={{ letterSpacing: '0.38em' }}
            >
              SILHOUETTE
            </span>
          </span>
        </div>

        <div className="relative z-10 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold-400/20 border border-gold-400/30 rounded-full">
            <Sparkles size={13} className="text-gold-400" />
            <span className="text-xs font-medium text-gold-300 tracking-wide uppercase">The Editorial Fashion Platform</span>
          </div>
          <h1 className="font-editorial text-4xl xl:text-5xl font-semibold leading-tight">
            Where fashion<br /><span className="text-gold-400">voices</span> shape<br />the narrative.
          </h1>
          <p className="text-charcoal-400 text-base leading-relaxed max-w-sm">
            Editorial perspectives, curated looks, and a community shaping the future of fashion.
          </p>
        </div>

        <div className="relative z-10 bg-white/5 border border-white/10 rounded-xl p-5">
          <div className="flex gap-1 mb-3">
            {[0,1,2,3,4].map((i) => <Star key={i} size={13} className="fill-gold-400 text-gold-400" />)}
          </div>
          <p className="text-sm text-charcoal-300 leading-relaxed italic font-editorial">
            "Silhouette is where real fashion conversations happen. No algorithms, just authentic voices."
          </p>
          <div className="flex items-center gap-2 mt-3">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gold-300 to-gold-500 flex items-center justify-center text-xs font-semibold text-charcoal-900">S</div>
            <div>
              <p className="text-xs font-semibold text-white">Silhouette Community</p>
              <p className="text-xs text-charcoal-400">Real users, real fashion</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 py-10 overflow-y-auto">
        <div className="w-full max-w-md mx-auto">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <span
              className="flex items-center gap-2.5 select-none"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              <span
                className="text-5xl font-bold text-charcoal-900"
                style={{ letterSpacing: '-0.02em', lineHeight: 1 }}
              >
                S
              </span>
              <span
                className="w-px h-6"
                style={{ background: 'rgba(100,100,100,0.25)' }}
                aria-hidden="true"
              />
              <span
                className="text-lg font-medium text-charcoal-900"
                style={{ letterSpacing: '0.38em' }}
              >
                SILHOUETTE
              </span>
            </span>
          </div>

          {/* Mode Toggle */}
          <div className="flex bg-cream-200 rounded-xl p-1 mb-8">
            <button onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === 'login' ? 'bg-white text-charcoal-900 shadow-sm' : 'text-charcoal-500 hover:text-charcoal-700'}`}>
              Sign In
            </button>
            <button onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${mode === 'signup' ? 'bg-white text-charcoal-900 shadow-sm' : 'text-charcoal-500 hover:text-charcoal-700'}`}>
              Create Account
            </button>
          </div>

          <div className="mb-7">
            <h2 className="font-editorial text-2xl font-semibold text-charcoal-900">
              {mode === 'login' ? 'Welcome back' : 'Join the syndicate'}
            </h2>
            <p className="text-sm text-charcoal-500 mt-1">
              {mode === 'login' ? 'Sign in to access your editorial dashboard' : 'Create your account and choose your role'}
            </p>
          </div>

          {mode === 'login' ? (
            <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-1.5" style={{ fontFamily: "'Cinzel', serif" }}>Email</label>
                <input
                  type="email"
                  {...loginForm.register('email', { required: 'Email is required' })}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 bg-white border border-cream-400 rounded-xl text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400 transition-all"
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.email.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-1.5" style={{ fontFamily: "'Cinzel', serif" }}>Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', { required: 'Password is required' })}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-11 bg-white border border-cream-400 rounded-xl text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400 transition-all"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-400 hover:text-charcoal-700">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.password.message}</p>
                )}
              </div>
              <button type="submit" disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-charcoal-900 text-white rounded-xl text-sm font-semibold hover:bg-charcoal-800 disabled:opacity-60 active:scale-[0.98] transition-all duration-150">
                {isLoading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                {isLoading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
              {/* Role Selection */}
              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-2" style={{ fontFamily: "'Cinzel', serif" }}>I am a…</label>
                <div className="grid grid-cols-2 gap-3">
                  {(['enthusiast', 'icon'] as UserRole[]).map((r) => (
                    <button key={r} type="button" onClick={() => { setSelectedRole(r); signupForm.setValue('role', r); }}
                      className={`p-3 rounded-xl border-2 text-left transition-all duration-150 ${selectedRole === r ? 'border-charcoal-900 bg-charcoal-900 text-white' : 'border-cream-400 bg-white text-charcoal-700 hover:border-charcoal-400'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {r === 'icon' && <Sparkles size={13} className={selectedRole === r ? 'text-gold-400' : 'text-gold-500'} />}
                        <span className="text-sm font-semibold capitalize">{r}</span>
                      </div>
                      <p className={`text-xs ${selectedRole === r ? 'text-charcoal-400' : 'text-charcoal-500'}`}>
                        {r === 'icon' ? 'Publish editorials & lookbooks' : 'Follow, save & discover'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1.5" style={{ fontFamily: "'Cinzel', serif" }}>Display Name</label>
                  <input type="text" {...signupForm.register('displayName', { required: 'Required' })}
                    placeholder="Your Name"
                    className="w-full px-3 py-2.5 bg-white border border-cream-400 rounded-xl text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400 transition-all" />
                  {signupForm.formState.errors.displayName && (
                    <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.displayName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1.5" style={{ fontFamily: "'Cinzel', serif" }}>Username</label>
                  <input type="text" {...signupForm.register('username', { required: 'Required' })}
                    placeholder="@handle"
                    className="w-full px-3 py-2.5 bg-white border border-cream-400 rounded-xl text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400 transition-all" />
                  {signupForm.formState.errors.username && (
                    <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.username.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-1.5" style={{ fontFamily: "'Cinzel', serif" }}>Email</label>
                <input type="email" {...signupForm.register('email', { required: 'Email is required' })}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 bg-white border border-cream-400 rounded-xl text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400 transition-all" />
                {signupForm.formState.errors.email && (
                  <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-1.5" style={{ fontFamily: "'Cinzel', serif" }}>Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'}
                    {...signupForm.register('password', { required: 'Password required', minLength: { value: 6, message: 'Min 6 characters' } })}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-11 bg-white border border-cream-400 rounded-xl text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400 transition-all" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-400 hover:text-charcoal-700">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal-700 mb-1.5" style={{ fontFamily: "'Cinzel', serif" }}>Confirm Password</label>
                <div className="relative">
                  <input type={showConfirm ? 'text' : 'password'}
                    {...signupForm.register('confirmPassword', { required: 'Please confirm password' })}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-11 bg-white border border-cream-400 rounded-xl text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400 transition-all" />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-400 hover:text-charcoal-700">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" {...signupForm.register('agreeTerms')}
                  className="mt-0.5 w-4 h-4 rounded border-cream-400 text-charcoal-900 focus:ring-gold-400/40" />
                <span className="text-xs text-charcoal-500 leading-relaxed">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setLegalDoc('terms')}
                    className="text-gold-500 hover:text-gold-600 underline underline-offset-2 decoration-gold-400/60 hover:decoration-gold-600 transition-colors duration-150 font-medium"
                  >
                    Terms of Service
                  </button>
                  {' '}and{' '}
                  <button
                    type="button"
                    onClick={() => setLegalDoc('privacy')}
                    className="text-gold-500 hover:text-gold-600 underline underline-offset-2 decoration-gold-400/60 hover:decoration-gold-600 transition-colors duration-150 font-medium"
                  >
                    Privacy Policy
                  </button>
                </span>
              </label>

              <button type="submit" disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 bg-charcoal-900 text-white rounded-xl text-sm font-semibold hover:bg-charcoal-800 disabled:opacity-60 active:scale-[0.98] transition-all duration-150">
                {isLoading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                {isLoading ? 'Creating account…' : 'Create Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}